"use client";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { UITheme } from "@/provider/data/type";
import { OptionsContext } from "@/provider/options-provider";
import { MagnifyingGlassIcon } from "@heroicons/react/20/solid";
import { useContext } from "react";
import { UseFormReturn } from "react-hook-form";
import { Spinner } from "./spinner";
import { FormControl, FormField, FormItem, FormMessage } from "./ui/form";
import { Input } from "./ui/input";
import { Label } from "./ui/label";

export function Search({
  form,
  className,
  icon = true,
  theme = "Shareholder2016",
  showSearch = true,
}: {
  form: UseFormReturn<any>;
  className?: string;
  icon?: boolean;
  theme?: UITheme;
  showSearch?: boolean;
}) {
  const { searchText, setSearchText, loadingResults } =
    useContext(OptionsContext);

  return (
    <Popover>
      <PopoverTrigger asChild>
        <div
          className={cn(
            "relative flex w-full flex-row items-center",
            className,
          )}
        >
          <Label htmlFor="search-field" className="sr-only">
            Search
          </Label>
          <MagnifyingGlassIcon
            className={cn(
              "pointer-events-none absolute inset-y-0 left-6 h-full w-5 text-gray-400 md:left-8 xl:left-2",
              icon ? "block" : "hidden",
            )}
            aria-hidden="true"
          />
          <FormField
            control={form.control}
            name="searchText"
            render={({ field }) => (
              <FormItem className={cn("flex w-full", icon ? "h-10" : "h-5")}>
                <FormControl>
                  <Input
                    {...field}
                    className={cn(
                      "focus:ring-3 search-field flex h-full w-full border-0 py-0 pl-8 pr-3 text-gray-900 ring-1 placeholder:text-gray-400 sm:text-sm",
                      icon
                        ? ""
                        : "rounded-none px-0 text-xsl font-bold placeholder:text-xsl placeholder:font-normal",
                      theme === "moonlight"
                        ? `bg-sh-moonlight-base text-white`
                        : theme === "highcontrast"
                          ? `bg-sh-highcontrast-base text-black`
                          : `bg-sh-shareholder2016-base text-black`,
                    )}
                    placeholder={icon ? "Keywords..." : "Enter Keywords..."}
                    type="search"
                    name="search"
                    autoComplete="off"
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          {loadingResults && icon && <Spinner className="ml-3" />}
        </div>
      </PopoverTrigger>
      <PopoverContent
        className={cn(
          "h-fit w-[var(--radix-popover-trigger-width)] bg-yellow-100 py-1 text-justify text-xs text-muted-foreground shadow-lg",
          icon ? "block" : "hidden",
        )}
        onOpenAutoFocus={(e) => {
          e.preventDefault();
        }}
      >
        Enclose with &quot;&quot; to look for an exact match. `+` for
        combination of words. `,` separated lists specify alternatives.
      </PopoverContent>
    </Popover>
  );
}
